import { useEffect, useState } from "react";
import { useT } from "@transifex/react";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { useBookContext } from "../../../context/BookContext";
import { useHistory } from "react-router-dom";
import StyledButton from "../../../basic/styledButton/StyledButton";
import ResponsiveButton from "../../../basic/responsiveButton/ResponsiveButton";
import CtasWrapper from "../../../wrapper/create/ctasWrapper/CtasWrapper";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import { CreationModes } from "../../../constants/CreationFlowConstants";
import AccordionTab from "../../../basic/accordionTab/AccordionTab";
import SelectBookStepperRT from "../selectBookStepperRT/SelectBookStepperRT";
import ExerciseSheetTitle from "../../../basic/create/ExerciseSheetTitle/ExerciseSheetTitle";
import CreationFlowSection from "../../../basic/create/creationFlowSection/CreationFlowSection";
import { StickyWrapper } from "../../../basic/accordionTab/StyledComponents";
import VocabTrainingCreationHeader from "../../../complex/create/vocabTrainingCreationHeader/VocabTrainingCreationHeader";
import TestCreationHeader from "../../../complex/create/testCreationHeader/TestCreationHeader";
import BookSelection from "../../../complex/create/bookSelection/BookSelection";

import { Wrapper } from "./StyledComponents";

/** The four-step book selection: the "stepper" variant of the book selection experiment. */
const SelectBookStepper = () => {
    const { vocabularyDrawerContent, testContent } = useTestCreationContext();

    const { setLastSourceSelected } = useBookContext();

    const { creatingMode, userInfo } = useGeneralContext();

    const t = useT();
    const t_back = t("Back", { _tags: "CreateTest,SelectBook" });

    const t_ChooseVocabulary = t("Choose vocabulary:", { _tags: "CreateTest,Tab" });
    const t_WhatToPractice = t("What should be practised?", { _tags: "CreateTest,Tab" });
    const t_DesignExerciseUnit = t("Design exercise unit:", { _tags: "CreateTest,Tab" });
    const t_HowToPractice = t("How should be practised?", { _tags: "CreateTest,Tab" });

    const history = useHistory();
    const [confirmButtonSlot, setConfirmButtonSlot] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        sessionStorage.removeItem("deepLinkInProgress");
    }, []);

    function handleBackButton() {
        history.goBack();
    }

    const renderAccordionTabVocabulary = () => {
        const usedVocabulary =
            Object.values(vocabularyDrawerContent).filter((word) => word.exerciseIds && word.exerciseIds.length > 0)
                .length ?? 0;
        const availableVocabulary = Object.values(vocabularyDrawerContent).length || 0;

        return (
            <AccordionTab
                mainIconName={"vocabulary-list-unset"}
                title={t_ChooseVocabulary}
                description={t_WhatToPractice}
                counterValue={"(" + usedVocabulary + " / " + availableVocabulary + ")"}
                showAccordionTabDetails
                isActive
            >
                <BookSelection confirmButtonSlot={confirmButtonSlot} />
            </AccordionTab>
        );
    };

    const renderAccordionTabExercises = () => {
        if (testContent.length > 0) {
            const amountExercises = testContent.length;

            return (
                <StickyWrapper>
                    <AccordionTab
                        mainIconName={"clipboard"}
                        title={t_DesignExerciseUnit}
                        description={t_HowToPractice}
                        counterValue={"(" + amountExercises + ")"}
                        onClickEdit={() => {
                            setLastSourceSelected(undefined);
                            history.push("/create/summary");
                        }}
                    >
                        <></>
                    </AccordionTab>
                </StickyWrapper>
            );
        } else {
            return <div />;
        }
    };

    if (creatingMode === CreationModes.RECURRING_TASK) {
        return (
            <VocabTrainingCreationHeader hasFullHeightContent>
                <SelectBookStepperRT />
            </VocabTrainingCreationHeader>
        );
    } else {
        return (
            <TestCreationHeader hasFullHeightContent>
                <Wrapper>
                    <CreationFlowSection>
                        <ExerciseSheetTitle />
                    </CreationFlowSection>
                    {renderAccordionTabVocabulary()}
                    {renderAccordionTabExercises()}
                </Wrapper>
                {/* Also for anonymous users, who get no back button: the book selection's confirm button lives here. */}
                <CtasWrapper isSticky>
                    {!userInfo.isAnonymousUser && (
                        <ResponsiveButton
                            onClick={handleBackButton}
                            iconName="chevron-left"
                            label={t_back}
                        >
                            <StyledButton
                                onClick={handleBackButton}
                                arrowDirection={"LEFT"}
                                buttonStyle={"BLANK"}
                            >
                                {t_back}
                            </StyledButton>
                        </ResponsiveButton>
                    )}
                    <div
                        ref={setConfirmButtonSlot}
                        className="ml-auto flex"
                    />
                </CtasWrapper>
            </TestCreationHeader>
        );
    }
};

export default SelectBookStepper;
